/* Manchester · Track B — Okendo widget ES-MX text fallback patcher.
   The proper fix is in the Okendo dashboard (Settings → Localization →
   set primary locale to es-MX). This file is a frontend safety net that
   replaces the most common English strings the widget emits before the
   merchant configures the locale, so MX customers don't see "Size
   Purchased" / "Age Range" / etc. while we wait for the config change.

   Strategy:
   1. MutationObserver on the reviews container — fires whenever Okendo
      renders new review cards (initial paint + filter/sort + load more).
   2. For each new text node, check against a known dictionary of EN→ES
      strings and replace in place.
   3. aria-label fallback: if a node has both EN visible text AND an
      English aria-label, swap both.

   Survives Okendo widget updates (no class-name fragility — just text). */

(function () {
  'use strict';

  // EN→ES dictionary. Keys are case-sensitive exact matches AFTER trim.
  // Add more as you discover them in the rendered widget.
  var DICT = {
    'Size Purchased': 'Talla comprada',
    'Age Range': 'Rango de edad',
    'Height': 'Estatura',
    'Fit': 'Ajuste',
    'Weight': 'Peso',
    'Body Type': 'Tipo de cuerpo',
    'Skin Tone': 'Tono de piel',
    'Verified Buyer': 'Comprador verificado',
    'Recommends this product': 'Recomienda este producto',
    "Doesn't recommend this product": 'No recomienda este producto',
    'Was this helpful?': '¿Fue útil esto?',
    'Helpful': 'Útil',
    'Report': 'Reportar',
    'Reply': 'Responder',
    'Read more': 'Leer más',
    'Read less': 'Leer menos',
    'Show more': 'Mostrar más',
    'Show less': 'Mostrar menos',
    'Load more': 'Cargar más',
    'Load more reviews': 'Cargar más reseñas',
    'View more reviews': 'Ver más reseñas',
    'Write a Review': 'Escribir una reseña',
    'Write a review': 'Escribir una reseña',
    'Sort': 'Ordenar',
    'Sort by': 'Ordenar por',
    'Filter': 'Filtrar',
    'Filters': 'Filtros',
    'Most Recent': 'Más recientes',
    'Most Helpful': 'Más útiles',
    'Highest Rating': 'Rating más alto',
    'Lowest Rating': 'Rating más bajo',
    'Photos & Videos': 'Fotos y vídeos',
    'With Photos': 'Con fotos',
    'With Videos': 'Con vídeos',
    'Verified Only': 'Solo verificadas',
    'All Ratings': 'Todas las calificaciones',
    'All Reviews': 'Todas las reseñas',
    'reviews': 'reseñas',
    'review': 'reseña',
    'Reviews': 'Reseñas',
    'Review': 'Reseña',
    '5 Stars': '5 estrellas',
    '4 Stars': '4 estrellas',
    '3 Stars': '3 estrellas',
    '2 Stars': '2 estrellas',
    '1 Star': '1 estrella',
    'out of 5 stars': 'de 5 estrellas',
    'Loading': 'Cargando',
    'Loading...': 'Cargando…',
    'Submit': 'Enviar',
    'Submit Review': 'Enviar reseña',
    'Cancel': 'Cancelar',
    'Continue': 'Continuar',
    'Next': 'Siguiente',
    'Previous': 'Anterior',
    'Page': 'Página',
    'of': 'de',
    'Yes': 'Sí',
    'No': 'No',
    'Anonymous': 'Anónimo',
    'A Manchester Customer': 'Un cliente de Manchester'
  };

  // Walk text nodes BUT skip any inside interactive elements (button/input/a)
  // — Okendo / React manage their own text inside those, and mutating them
  // breaks reconciliation (clicks register but state doesn't propagate).
  // Also use data-mfg-i18n-done on parents to avoid re-mutating on every
  // MutationObserver fire (idempotent).
  function isInsideInteractive(node) {
    var p = node.parentNode;
    while (p && p.nodeType === 1) {
      var tag = p.tagName;
      if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' ||
          tag === 'SELECT' || tag === 'TEXTAREA' ||
          p.hasAttribute('contenteditable') || p.getAttribute('role') === 'button') {
        return true;
      }
      if (p.dataset && p.dataset.mfgI18nDone === '1') return true;
      p = p.parentNode;
    }
    return false;
  }

  function replaceTextNodes(root) {
    if (!root || root.nodeType !== 1) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var n;
    var batch = [];
    while ((n = walker.nextNode())) {
      var v = n.nodeValue;
      if (!v) continue;
      var t = v.trim();
      if (!t || t.length > 60) continue;
      if (!DICT[t]) continue;
      if (isInsideInteractive(n)) continue; // don't disrupt React state
      batch.push({ node: n, value: v.replace(t, DICT[t]) });
    }
    batch.forEach(function (item) {
      item.node.nodeValue = item.value;
      // mark parent so we skip on subsequent observer fires
      if (item.node.parentNode && item.node.parentNode.dataset) {
        item.node.parentNode.dataset.mfgI18nDone = '1';
      }
    });

    // aria-label patches: skip if element is interactive (Okendo manages it)
    var aria = root.querySelectorAll('[aria-label]:not(button):not(a):not(input):not([role="button"])');
    aria.forEach(function (el) {
      if (el.dataset.mfgI18nAriaDone === '1') return;
      var a = el.getAttribute('aria-label').trim();
      if (DICT[a]) {
        el.setAttribute('aria-label', DICT[a]);
        el.dataset.mfgI18nAriaDone = '1';
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll(
      'okendo-reviews-product-listing, ' +
      '.okendo_row, ' +
      '.oke-w, ' +
      '.okeReviews, ' +
      '[class*="okendo-reviews"]'
    );
    roots.forEach(replaceTextNodes);
  }

  function watch() {
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1) replaceTextNodes(n);
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); watch(); });
  } else {
    init();
    watch();
  }
  // Run a few more times for late-loading widgets
  setTimeout(init, 500);
  setTimeout(init, 1500);
  setTimeout(init, 3500);
})();
